/**
 * useInstantMessaging — shared hook providing socket.io connection state,
 * conversations, online presence, unread counter and dnd toggle for the
 * pro user's inter-PS instant messaging.
 *
 * One instance per app is enough (we mount it from ImProvider).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { API, BACKEND_URL } from "../apiBase";
import { useProAuth } from "../components/ProAuthContext";

export function useInstantMessagingClient() {
  const { user: proUser, token: proToken } = useProAuth();
  const [connected, setConnected] = useState(false);
  const [conversations, setConversations] = useState([]); // server-sorted, last_message_at desc
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [dnd, setDnd] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messagesByConv, setMessagesByConv] = useState({}); // {convId: [msg]}
  const socketRef = useRef(null);
  const messagesByConvRef = useRef(messagesByConv);

  // Keep ref in sync so socket handlers always see latest state without re-binding.
  useEffect(() => { messagesByConvRef.current = messagesByConv; }, [messagesByConv]);

  // ---------- REST loaders -------------------------------------------------
  const reloadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/im/conversations`);
      setConversations(Array.isArray(data) ? data : []);
      // Seed presence from server view.
      const set = new Set();
      (data || []).forEach((c) => { if (c.other_online && c.other_user_id) set.add(c.other_user_id); });
      setOnlineUsers((prev) => {
        const merged = new Set(prev);
        set.forEach((id) => merged.add(id));
        return merged;
      });
    } catch (_) {
      // 401 — user not logged in pro: silently bail.
    }
  }, []);

  const reloadDnd = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/im/dnd`);
      setDnd(!!data?.dnd);
    } catch (_) {
      /* silently ignore — endpoint requires auth */
    }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await axios.get(`${API}/im/conversations/${convId}/messages?limit=200`);
      setMessagesByConv((prev) => ({ ...prev, [convId]: Array.isArray(data) ? data : [] }));
      // Mark all as read on the conv list locally.
      setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, unread: 0 } : c));
    } catch (_) {
      /* ignore */
    }
  }, []);

  const openConversation = useCallback(async (otherUserId) => {
    const { data } = await axios.post(`${API}/im/conversations`, { other_user_id: otherUserId });
    await reloadConversations();
    return data.id;
  }, [reloadConversations]);

  const sendMessage = useCallback(async (convId, content) => {
    const trimmed = (content || "").trim();
    if (!trimmed || !convId) return;
    const { data } = await axios.post(`${API}/im/conversations/${convId}/messages`, { content: trimmed });
    // Optimistic insert (socket may also deliver — dedupe by id).
    setMessagesByConv((prev) => {
      const arr = prev[convId] || [];
      if (arr.some((m) => m.id === data.id)) return prev;
      return { ...prev, [convId]: [...arr, data] };
    });
    setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, last_message_preview: trimmed.slice(0, 120), last_message_at: data.created_at } : c));
    return data;
  }, []);

  const toggleDnd = useCallback(async () => {
    const newVal = !dnd;
    setDnd(newVal);
    try {
      await axios.put(`${API}/im/dnd`, { dnd: newVal });
    } catch (_) {
      setDnd(!newVal);
    }
  }, [dnd]);

  // ---------- Socket.io ----------------------------------------------------
  useEffect(() => {
    // Wait until the pro user is authenticated; otherwise the socket auth would fail.
    if (!proToken) {
      setConnected(false);
      return;
    }
    const socket = io(BACKEND_URL, {
      path: "/api/socket.io/",
      transports: ["websocket", "polling"],
      auth: { token: proToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1500,
    });
    socketRef.current = socket;

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    socket.on("presence:online", ({ user_id }) => {
      if (!user_id) return;
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.add(user_id);
        return next;
      });
    });
    socket.on("presence:offline", ({ user_id }) => {
      if (!user_id) return;
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(user_id);
        return next;
      });
    });

    socket.on("message:new", (msg) => {
      if (!msg?.conversation_id) return;
      setMessagesByConv((prev) => {
        const arr = prev[msg.conversation_id] || [];
        if (arr.some((m) => m.id === msg.id)) return prev;
        return { ...prev, [msg.conversation_id]: [...arr, msg] };
      });
      setConversations((prev) => {
        const list = [...prev];
        const idx = list.findIndex((c) => c.id === msg.conversation_id);
        if (idx >= 0) {
          const existing = list[idx];
          // If the message is not from me AND this conv is not active → bump unread.
          // We can't reliably know `me.id` here, so trust the server's count refresh on reload.
          const incrementUnread = existing.id !== activeConversationId;
          list[idx] = {
            ...existing,
            last_message_preview: (msg.content || "").slice(0, 120),
            last_message_at: msg.created_at,
            unread: incrementUnread ? (existing.unread || 0) + 1 : 0,
          };
          // Move to top.
          const [hit] = list.splice(idx, 1);
          list.unshift(hit);
        } else {
          // Unknown conv → trigger a reload to fetch it.
          reloadConversations();
        }
        return list;
      });
    });

    socket.on("message:read", ({ conversation_id }) => {
      // The OTHER side just read everything — update local message read_by flags.
      setMessagesByConv((prev) => {
        const arr = prev[conversation_id];
        if (!arr) return prev;
        return { ...prev, [conversation_id]: arr.map((m) => ({ ...m, _peer_read: true })) };
      });
    });

    return () => {
      socket.off();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [proToken, activeConversationId, reloadConversations]);

  // Initial loads when pro user is authenticated (re-runs on login/logout).
  useEffect(() => {
    if (!proUser?.id) {
      // On logout, clear local state.
      setConversations([]);
      setMessagesByConv({});
      setOnlineUsers(new Set());
      setActiveConversationId(null);
      return;
    }
    reloadConversations();
    reloadDnd();
  }, [proUser?.id, reloadConversations, reloadDnd]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unread || 0), 0),
    [conversations]
  );

  return {
    connected,
    conversations,
    onlineUsers,
    dnd,
    toggleDnd,
    activeConversationId,
    setActiveConversationId,
    messagesByConv,
    totalUnread,
    reloadConversations,
    loadMessages,
    openConversation,
    sendMessage,
  };
}
